import { createContext, useContext, useEffect, useState, type PropsWithChildren } from "react";
import { api } from "../lib/api";
import type { SessionUser } from "../../shared/types";

interface AuthValue {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try { setUser(await api<SessionUser>("/api/v1/auth/me")); }
    catch { setUser(null); }
    finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, []);

  async function login(email: string, password: string) {
    await api("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    setUser(await api<SessionUser>("/api/v1/auth/me"));
  }

  async function logout() {
    await api("/api/v1/auth/logout", { method: "POST" });
    setUser(null);
  }

  return <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
